import { useMemo, useState } from 'react';
import { Plus, Trash2, Pencil, FlaskConical, X, RefreshCw, Tag as TagIcon, Sparkles, Shield } from 'lucide-react';
import { Section, SettingRow, Toolbar } from '@vnetwork/ui/layout';
import {
  Card,
  CardBody,
  Button,
  IconButton,
  Input,
  Switch,
  Badge,
  Checkbox,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { DataTable, StatusPill, Stat, StatGroup, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { toneColor, formatDate } from '../../shared';
import { EdgePipelineExplainer } from '../../_shared/EdgePipeline';
import {
  useFirewallRules,
  useSaveFirewallRule,
  useDeleteFirewallRule,
  useToggleFirewall,
  useTestFirewall,
  type FirewallRule,
} from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import { CdnAspectDrift } from '../../_shared/VendorDrift';
import {
  FIELD_META, FIELDS, OP_LABELS, TRANSFORMS, ACTIONS, NO_VALUE_OPS,
  TEXT_OPS,
  actionTone,
  mkCond, mkGroup, blankForm, toForm, toWire, ruleInput, ruleSummary,
  TEMPLATES,
  type FCond, type FForm,
} from './firewall-rule-model';

const MAX_RULES = 200;
const MAX_CONDITIONS = 64;

export function FirewallRulesTab({ domain, domainId }: { domain: string; domainId: string }) {
  const { show } = useToast();
  const { data, isLoading, refetch, isFetching } = useFirewallRules(domainId);
  const rules = data?.rules ?? [];
  const enabled = data?.enabled ?? false;

  const [editing, setEditing] = useState<FForm | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FirewallRule | null>(null);
  const [testOpen, setTestOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = useToggleFirewall(domainId, {
    onSuccess: () => show({ tone: 'success', title: 'Firewall toggled' }),
    onError: (e) => show({ tone: 'danger', title: 'Could not toggle', description: errorMessage(e, 'Could not toggle') }),
  });
  const save = useSaveFirewallRule(domainId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rule saved' }); setEditing(null); },
    onError: (e) => show({ tone: 'danger', title: 'Could not save rule', description: errorMessage(e, 'Could not save rule') }),
  });
  const quietSave = useSaveFirewallRule(domainId, { onError: (e) => show({ tone: 'danger', title: 'Could not update rule', description: errorMessage(e, 'Could not update rule') }) });
  const remove = useDeleteFirewallRule(domainId, {
    onSuccess: () => { trackFeatureAction('cdn', 'firewall_rule', 'delete'); show({ tone: 'success', title: 'Rule deleted' }); setPendingDelete(null); },
    onError: (e) => show({ tone: 'danger', title: 'Could not delete rule', description: errorMessage(e, 'Could not delete rule') }),
  });

  const stats = useMemo(() => ({
    total: rules.length,
    enabled: rules.filter((r) => r.enabled).length,
    block: rules.filter((r) => r.action === 'block' || r.action === 'timed_block').length,
    allow: rules.filter((r) => r.action === 'allow' || r.action === 'bypass_waf').length,
  }), [rules]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rules.filter((r) => {
      if (actionFilter !== 'all' && r.action !== actionFilter) return false;
      if (stateFilter === 'enabled' && !r.enabled) return false;
      if (stateFilter === 'disabled' && r.enabled) return false;
      if (!q) return true;
      return [r.name, r.description, r.action, ...(r.tags || [])].join(' ').toLowerCase().includes(q);
    });
  }, [rules, search, actionFilter, stateFilter]);

  const toggleRule = (r: FirewallRule, v: boolean) => quietSave.mutate(ruleInput(r, { enabled: v }));
  const bulkSet = (v: boolean) => {
    filtered.filter((r) => selected.has(String(r.id))).forEach((r) => quietSave.mutate(ruleInput(r, { enabled: v })));
    setSelected(new Set());
  };
  const allChecked = filtered.length > 0 && filtered.every((r) => selected.has(String(r.id)));
  const someChecked = filtered.some((r) => selected.has(String(r.id)));

  const columns = useMemo<ColumnDef<FirewallRule, unknown>[]>(() => [
    {
      id: 'select', enableSorting: false,
      header: () => (
        <Checkbox
          aria-label="Select all"
          checked={allChecked ? true : someChecked ? 'indeterminate' : false}
          onCheckedChange={(v) => setSelected(v ? new Set(filtered.map((r) => String(r.id))) : new Set())}
        />
      ),
      cell: ({ row }) => {
        const id = String(row.original.id);
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              aria-label={`Select ${row.original.name}`}
              checked={selected.has(id)}
              onCheckedChange={(v) => setSelected((s) => { const n = new Set(s); if (v) n.add(id); else n.delete(id); return n; })}
            />
          </div>
        );
      },
    },
    { id: 'priority', header: 'Priority', cell: ({ row }) => <Badge tone="neutral" appearance="subtle" size="sm">{row.original.priority}</Badge> },
    {
      id: 'name', header: 'Name',
      cell: ({ row }) => {
        const r = row.original;
        return (
          <div style={{ display: 'grid', gap: 'var(--vn-space-1)', minWidth: 0 }}>
            <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{r.name}</span>
            {r.description ? <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 360 }}>{r.description}</span> : null}
            {(r.tags?.length ?? 0) > 0 ? (
              <span style={{ display: 'flex', gap: 'var(--vn-space-1)', flexWrap: 'wrap' }}>
                {r.tags!.map((t) => <Badge key={t} tone="neutral" appearance="outline" size="sm">{t}</Badge>)}
              </span>
            ) : null}
          </div>
        );
      },
    },
    { id: 'action', header: 'Action', cell: ({ row }) => <Badge tone={actionTone(row.original.action)} appearance="subtle" size="sm">{row.original.action}</Badge> },
    { id: 'conds', header: 'Cond.', cell: ({ row }) => <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--vn-fg-muted)' }}>{row.original.conditions?.length ?? 0}</span> },
    { id: 'hits', header: 'Hits', cell: ({ row }) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.original.hits ?? 0}</span> },
    { id: 'lasthit', header: 'Last hit', cell: ({ row }) => <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{row.original.last_hit ? formatDate(row.original.last_hit) : 'never'}</span> },
    {
      id: 'enabled', header: 'Enabled',
      cell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Switch size="sm" checked={row.original.enabled} onCheckedChange={(v) => toggleRule(row.original, v)} aria-label="Enabled" />
        </div>
      ),
    },
    {
      id: 'actions', header: '', enableSorting: false,
      cell: ({ row }) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-1)' }} onClick={(e) => e.stopPropagation()}>
          <IconButton variant="ghost" size="sm" aria-label="Edit" onClick={() => setEditing(toForm(row.original))}><Pencil size={15} /></IconButton>
          <IconButton variant="ghost" size="sm" aria-label="Delete" onClick={() => setPendingDelete(row.original)}><Trash2 size={15} /></IconButton>
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [filtered, selected, allChecked, someChecked]);

  const nextPriority = rules.length ? Math.max(...rules.map((r) => r.priority)) + 10 : 100;

  return (
    <Section
      title="Firewall Rules"
      description={`Tier 0.5 — structured condition rules for ${domain}. AND within a group, OR between groups.`}
      actions={<StatusPill tone={enabled ? 'success' : 'muted'}>{enabled ? 'Enabled' : 'Disabled'}</StatusPill>}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <CdnAspectDrift domainId={domainId} path="firewall-rules" />
        <EdgePipelineExplainer />

        {/* Global toggle */}
        <Card flat>
          <CardBody>
            <SettingRow
              title={`Firewall Rules: ${enabled ? 'ENABLED' : 'DISABLED'}`}
              description="Hot-path collapses to a single check (~1ns) when disabled. Saved rules are preserved."
              control={<Switch checked={enabled} disabled={toggle.isPending} onCheckedChange={(v) => toggle.mutate(v, { onSuccess: () => trackFeatureAction('cdn', 'firewall', v ? 'enable' : 'disable') })} />}
            />
          </CardBody>
        </Card>

        {/* Stats */}
        <Card flat>
          <CardBody>
            <StatGroup columns={4}>
              <Stat label="Total rules" value={stats.total} />
              <Stat label="Enabled" value={stats.enabled} dotColor={toneColor('success')} />
              <Stat label="Block actions" value={stats.block} dotColor={toneColor('danger')} />
              <Stat label="Allow / Bypass" value={stats.allow} dotColor={toneColor('info')} />
            </StatGroup>
          </CardBody>
        </Card>

        {/* Toolbar */}
        <Toolbar
          start={
            <Input
              placeholder="Search by name, tag, action, description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 340 }}
            />
          }
          end={
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger style={{ minWidth: 0, width: 150, maxWidth: '100%' }}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  {ACTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={stateFilter} onValueChange={setStateFilter}>
                <SelectTrigger style={{ minWidth: 0, width: 140, maxWidth: '100%' }}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All states</SelectItem>
                  <SelectItem value="enabled">Enabled</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
              <span style={{ fontSize: 'var(--vn-font-size-md)', color: 'var(--vn-fg-muted)' }}>{filtered.length} rules</span>
              <IconButton variant="ghost" size="sm" aria-label="Refresh" disabled={isFetching} onClick={() => refetch()}><RefreshCw size={15} /></IconButton>
              <Button variant="secondary" startIcon={<FlaskConical size={15} />} onClick={() => setTestOpen(true)}>Test</Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" startIcon={<Sparkles size={15} />}>Templates</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Start from a template</DropdownMenuLabel>
                  {TEMPLATES.map((t) => <DropdownMenuItem key={t.name} onSelect={() => setEditing(t.make(nextPriority))}>{t.name}</DropdownMenuItem>)}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button startIcon={<Plus size={15} />} disabled={rules.length >= MAX_RULES} onClick={() => setEditing(blankForm(nextPriority))}>Create rule</Button>
            </div>
          }
        />

        {someChecked ? (
          <Alert tone="info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-3)' }}>
            <span>{filtered.filter((r) => selected.has(String(r.id))).length} selected</span>
            <span style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
              <Button size="sm" variant="secondary" onClick={() => bulkSet(true)}>Enable</Button>
              <Button size="sm" variant="secondary" onClick={() => bulkSet(false)}>Disable</Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
            </span>
          </Alert>
        ) : null}

        <Card flat>
          <CardBody style={{ padding: 0 }}>
            {isLoading ? (
              <div style={{ padding: 'var(--vn-space-4)' }}><Skeleton style={{ height: 140 }} /></div>
            ) : (
              <DataTable
                columns={columns}
                data={filtered}
                getRowId={(r) => String(r.id)}
                onRowClick={(r) => setEditing(toForm(r))}
                emptyState={
                  // No rules at all → first-use with a create CTA; rules present but
                  // filtered out → plain no-match line.
                  rules.length === 0 ? (
                    <EmptyState
                      size="sm"
                      icon={<Shield size={28} />}
                      title="No firewall rules"
                      description="Add a rule to match and act on requests at the edge."
                      actions={<Button startIcon={<Plus size={15} />} onClick={() => setEditing(blankForm(100))}>Create rule</Button>}
                    />
                  ) : (
                    <EmptyState
                      size="sm"
                      icon={<Shield size={20} />}
                      title="No results"
                      description="No rules match the current filters."
                    />
                  )
                }
              />
            )}
          </CardBody>
        </Card>

        <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-subtle)', textAlign: 'right' }}>Max {MAX_RULES} rules · {MAX_CONDITIONS} conditions each</span>
      </div>

      {editing && <FirewallRuleDialog initial={editing} saving={save.isPending} onCancel={() => setEditing(null)} onSave={(f) => save.mutate(toWire(f), { onSuccess: () => trackFeatureAction('cdn', 'firewall_rule', f.id ? 'edit' : 'create') })} />}
      {testOpen && <FirewallTestDrawer domainId={domainId} onClose={() => setTestOpen(false)} />}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete firewall rule?"
        description={`Remove "${pendingDelete?.name}"?`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </Section>
  );
}

// ── Condition value input (field/operator aware) ───────────────────────────
function ValueInput({ cond, onChange }: { cond: FCond; onChange: (v: string) => void }) {
  const meta = FIELD_META[cond.field];
  if (NO_VALUE_OPS.includes(cond.operator)) return null;
  if (meta?.kind === 'boolean') {
    return (
      <Select value={cond.value === 'false' ? 'false' : 'true'} onValueChange={onChange}>
        <SelectTrigger style={{ width: 120 }}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="true">true</SelectItem><SelectItem value="false">false</SelectItem></SelectContent>
      </Select>
    );
  }
  if (meta?.kind === 'select' && !['in', 'not_in'].includes(cond.operator)) {
    return (
      <Select value={cond.value || meta.options![0]} onValueChange={onChange}>
        <SelectTrigger style={{ flex: 1, minWidth: 120 }}><SelectValue /></SelectTrigger>
        <SelectContent>{meta.options!.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  return (
    <Input
      style={{ flex: 1, minWidth: 120 }}
      type={meta?.kind === 'number' ? 'number' : 'text'}
      placeholder={['in', 'not_in'].includes(cond.operator) ? 'comma,separated' : meta?.kind === 'ip' ? '1.2.3.4 or 10.0.0.0/8' : 'value'}
      value={cond.value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function FirewallRuleDialog({ initial, saving, onCancel, onSave }: { initial: FForm; saving: boolean; onCancel: () => void; onSave: (f: FForm) => void }) {
  const [f, setF] = useState<FForm>(initial);
  const [tagDraft, setTagDraft] = useState('');
  const set = <K extends keyof FForm>(k: K, v: FForm[K]) => setF((p) => ({ ...p, [k]: v }));
  const patchCond = (gi: number, cid: string, c: Partial<FCond>) =>
    set('groups', f.groups.map((g, i) => (i === gi ? { ...g, conditions: g.conditions.map((x) => (x._id === cid ? { ...x, ...c } : x)) } : g)));
  const onFieldChange = (gi: number, cid: string, field: string) => {
    const meta = FIELD_META[field];
    const op = meta.ops[0];
    const value = meta.kind === 'boolean' ? 'true' : meta.kind === 'select' ? meta.options![0] : '';
    patchCond(gi, cid, { field, operator: op, value, field_key: '' });
  };
  const onOpChange = (gi: number, cid: string, operator: string) => patchCond(gi, cid, { operator });
  const addCond = (gi: number) => set('groups', f.groups.map((g, i) => (i === gi ? { ...g, conditions: [...g.conditions, mkCond()] } : g)));
  const removeCond = (gi: number, cid: string) => set('groups', f.groups.map((g, i) => (i === gi ? { ...g, conditions: g.conditions.filter((x) => x._id !== cid) } : g)).filter((g) => g.conditions.length));
  const addGroup = () => set('groups', [...f.groups, mkGroup()]);
  const removeGroup = (gi: number) => set('groups', f.groups.filter((_, i) => i !== gi));
  const toggleTransform = (gi: number, cid: string, t: string) => {
    const cond = f.groups[gi].conditions.find((x) => x._id === cid)!;
    patchCond(gi, cid, { transforms: cond.transforms.includes(t) ? cond.transforms.filter((x) => x !== t) : [...cond.transforms, t] });
  };
  const addTag = () => {
    const t = tagDraft.trim();
    if (t && !f.tags.includes(t) && f.tags.length < 16) set('tags', [...f.tags, t]);
    setTagDraft('');
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent style={{ maxWidth: 860 }}>
        <DialogHeader>
          <DialogTitle>{f.id ? 'Edit firewall rule' : 'New firewall rule'}</DialogTitle>
          <DialogDescription>Match requests with grouped conditions (OR between groups, AND within) and apply an action.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
              <Field label="Name" required>{(p) => <Input {...p} value={f.name} onChange={(e) => set('name', e.target.value)} />}</Field>
              <Field label="Priority" description="Lower runs first">{(p) => <Input {...p} type="number" value={String(f.priority)} onChange={(e) => set('priority', Number(e.target.value) || 0)} />}</Field>
              <Field label="Action">
                {(p) => (
                  <Select value={f.action} onValueChange={(v) => set('action', v)}>
                    <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                    <SelectContent>{ACTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </Field>
              {(f.action === 'block' || f.action === 'redirect' || f.action === 'timed_block') && (
                <Field label="Status code">{(p) => <Input {...p} type="number" value={String(f.status_code)} onChange={(e) => set('status_code', Number(e.target.value) || 0)} />}</Field>
              )}
              {f.action === 'redirect' && <Field label="Redirect URL">{(p) => <Input {...p} value={f.redirect_url} onChange={(e) => set('redirect_url', e.target.value)} />}</Field>}
              {f.action === 'timed_block' && <Field label="Duration (s)">{(p) => <Input {...p} type="number" value={String(f.duration)} onChange={(e) => set('duration', Number(e.target.value) || 0)} />}</Field>}
            </div>
            <Field label="Description">{(p) => <Input {...p} value={f.description} onChange={(e) => set('description', e.target.value)} />}</Field>

            {/* Tags */}
            <Field label="Tags" description="Press Enter to add (max 16)">
              {(p) => (
                <div style={{ display: 'flex', gap: 'var(--vn-space-1)', flexWrap: 'wrap', alignItems: 'center' }}>
                  {f.tags.map((t) => (
                    <Badge key={t} tone="neutral" appearance="outline" size="sm">
                      {t}
                      <IconButton variant="ghost" size="sm" aria-label={`Remove ${t}`} onClick={() => set('tags', f.tags.filter((x) => x !== t))} style={{ marginLeft: 'var(--vn-space-1)', padding: 0, lineHeight: 1, height: 'auto', width: 'auto', minWidth: 0, color: 'var(--vn-fg-muted)' }}>
                        <X size={12} />
                      </IconButton>
                    </Badge>
                  ))}
                  <Input
                    {...p}
                    startSlot={<TagIcon size={14} />}
                    style={{ width: 160 }}
                    value={tagDraft}
                    onChange={(e) => setTagDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  />
                </div>
              )}
            </Field>

            {/* Condition builder */}
            <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              {f.groups.map((g, gi) => (
                <div key={g._id} style={{ position: 'relative', border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-md)', padding: 'var(--vn-space-3)' }}>
                  {gi > 0 && <span style={{ position: 'absolute', top: -10, left: 12, fontSize: 'var(--vn-font-size-xs)', fontWeight: 'var(--vn-font-weight-bold)', color: toneColor('info', 'text'), background: 'var(--vn-bg-surface)', padding: '0 var(--vn-space-1)' }}>OR</span>}
                  {f.groups.length > 1 && (
                    <IconButton variant="ghost" size="sm" aria-label="Remove group" style={{ position: 'absolute', top: 8, right: 8 }} onClick={() => removeGroup(gi)}><Trash2 size={14} /></IconButton>
                  )}
                  <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                    {g.conditions.map((c, ci) => {
                      const meta = FIELD_META[c.field];
                      return (
                        <div key={c._id} style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
                          <div style={{ display: 'flex', gap: 'var(--vn-space-1)', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ width: 32, fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-bold)', color: 'var(--vn-fg-muted)' }}>{ci === 0 ? 'If' : 'AND'}</span>
                            <Select value={c.field} onValueChange={(v) => onFieldChange(gi, c._id, v)}>
                              <SelectTrigger style={{ flex: 1, minWidth: 120, maxWidth: '100%' }}><SelectValue /></SelectTrigger>
                              <SelectContent>{FIELDS.map((x) => <SelectItem key={x} value={x}>{FIELD_META[x].label}</SelectItem>)}</SelectContent>
                            </Select>
                            {c.field === 'http_request_headers' && <Input style={{ width: 120 }} placeholder="header name" value={c.field_key} onChange={(e) => patchCond(gi, c._id, { field_key: e.target.value })} />}
                            <Select value={c.operator} onValueChange={(v) => onOpChange(gi, c._id, v)}>
                              <SelectTrigger style={{ width: 150 }}><SelectValue /></SelectTrigger>
                              <SelectContent>{(meta?.ops ?? TEXT_OPS).map((x) => <SelectItem key={x} value={x}>{OP_LABELS[x] ?? x}</SelectItem>)}</SelectContent>
                            </Select>
                            <ValueInput cond={c} onChange={(v) => patchCond(gi, c._id, { value: v })} />
                            <IconButton variant="ghost" size="sm" aria-label="Remove condition" onClick={() => removeCond(gi, c._id)}><X size={15} /></IconButton>
                          </div>
                          {meta?.kind === 'text' && !NO_VALUE_OPS.includes(c.operator) && (
                            <div style={{ display: 'flex', gap: 'var(--vn-space-3)', alignItems: 'center', marginLeft: 38, flexWrap: 'wrap' }}>
                              <label style={{ display: 'flex', gap: 'var(--vn-space-1)', alignItems: 'center', fontSize: 'var(--vn-font-size-sm)' }}>
                                <Switch size="sm" checked={c.negate} onCheckedChange={(v) => patchCond(gi, c._id, { negate: v })} /> negate
                              </label>
                              <label style={{ display: 'flex', gap: 'var(--vn-space-1)', alignItems: 'center', fontSize: 'var(--vn-font-size-sm)' }}>
                                <Switch size="sm" checked={c.case_insensitive} onCheckedChange={(v) => patchCond(gi, c._id, { case_insensitive: v })} /> ignore case
                              </label>
                              <Select value="" onValueChange={(v) => toggleTransform(gi, c._id, v)}>
                                <SelectTrigger style={{ width: 170 }}><SelectValue placeholder={`transforms (${c.transforms.length})`} /></SelectTrigger>
                                <SelectContent>{TRANSFORMS.map((t) => <SelectItem key={t} value={t}>{c.transforms.includes(t) ? `✓ ${t}` : t}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: 'var(--vn-space-2)', marginLeft: 38 }}>
                    <Button variant="ghost" size="sm" startIcon={<Plus size={14} />} onClick={() => addCond(gi)}>AND condition</Button>
                  </div>
                </div>
              ))}
              <div><Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={addGroup}>OR group</Button></div>
            </div>

            {/* Live summary */}
            <Alert tone="info" icon={null}>
              <span style={{ fontSize: 'var(--vn-font-size-md)' }}>{ruleSummary(f)}</span>
            </Alert>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button disabled={!f.name.trim() || saving} onClick={() => onSave(f)}>{saving ? 'Saving…' : 'Save rule'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FirewallTestDrawer({ domainId, onClose }: { domainId: string; onClose: () => void }) {
  const [req, setReq] = useState({ method: 'GET', path: '/', query: '', user_agent: '', referer: '', content_type: '', client_ip: '', is_ssl: true, country_code: '', asn: 0, threat_score: 0 });
  const test = useTestFirewall(domainId);
  const set = <K extends keyof typeof req>(k: K, v: (typeof req)[K]) => setReq((r) => ({ ...r, [k]: v }));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={{ maxWidth: 560 }}>
        <DialogHeader>
          <DialogTitle>Test firewall rules</DialogTitle>
          <DialogDescription>Simulate a request and see which rule (if any) matches.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
              <Field label="Method">{(p) => <Input {...p} value={req.method} onChange={(e) => set('method', e.target.value)} />}</Field>
              <Field label="Path">{(p) => <Input {...p} value={req.path} onChange={(e) => set('path', e.target.value)} />}</Field>
              <Field label="Client IP">{(p) => <Input {...p} value={req.client_ip} onChange={(e) => set('client_ip', e.target.value)} />}</Field>
              <Field label="Country">{(p) => <Input {...p} value={req.country_code} onChange={(e) => set('country_code', e.target.value)} />}</Field>
              <Field label="User-Agent">{(p) => <Input {...p} value={req.user_agent} onChange={(e) => set('user_agent', e.target.value)} />}</Field>
              <Field label="Threat score">{(p) => <Input {...p} type="number" value={String(req.threat_score)} onChange={(e) => set('threat_score', Number(e.target.value) || 0)} />}</Field>
            </div>
            <SettingRow title="HTTPS" control={<Switch checked={req.is_ssl} onCheckedChange={(v) => set('is_ssl', v)} />} />
            {test.data && (
              <Alert tone={test.data.matched ? 'warning' : 'success'}>
                {test.data.matched ? `Matched "${test.data.rule_name}" → ${test.data.action}` : 'No rule matched — request would pass.'}
              </Alert>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button disabled={test.isPending} onClick={() => test.mutate(req)}>{test.isPending ? 'Testing…' : 'Run test'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
